import { DataTable } from 'mantine-datatable';
import { useLayoutEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

import { ActionIcon, Button, Group, Switch } from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { dateToDateHourString } from '@utils/date';

import {
  Coupon,
  useDeleteCouponMutation,
  useGetAllCouponsQuery,
  useUpdateCouponMutation,
} from '@api/coupons.api';

import UpsertCouponModal from '@components/CreateUpdateCouponModal';
import { LayoutContextType } from '@components/layout/Layout';

export default function Coupons() {
  // ==========================================================================
  // State
  // ==========================================================================

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data = [], isLoading } = useGetAllCouponsQuery();
  const [updateCoupon, { isLoading: isLoadingUpdate }] =
    useUpdateCouponMutation();
  const [deleteCoupon, { isLoading: isLoadingDelete }] =
    useDeleteCouponMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleCreateCouponClick = () => {
    openModal({
      title: 'Crea codice sconto',
      children: <UpsertCouponModal />,
    });
  };

  const handleUpdateCouponClick = (coupon: Coupon) => {
    openModal({
      title: 'Modifica codice sconto',
      children: <UpsertCouponModal coupon={coupon} />,
    });
  };

  const handleToggleCouponEnabled = (code: string, enable: boolean) => {
    openConfirmModal({
      title: `${enable ? 'Attiva' : 'Disattiva'} codice sconto`,
      children: enable
        ? 'Attivando il codice sconto gli utenti potranno nuovamente utilizzarlo. Proseguire?'
        : 'Disattivando il codice sconto gli utenti non potranno più utilizzarlo. Sarà possibile riattivarlo in qualsiasi momento. Proseguire?',
      labels: {
        cancel: 'Annulla',
        confirm: `${enable ? 'Attiva' : 'Disattiva'} codice sconto`,
      },
      confirmProps: { color: enable ? 'brand' : 'red' },
      onConfirm: () => updateCoupon({ code, body: { enabled: enable } }),
    });
  };

  const handleDeleteCouponClick = (code: string) => {
    openConfirmModal({
      title: 'Elimina codice sconto',
      children:
        'Eliminando il codice sconto gli utenti non potranno più utilizzarlo. Proseguire?',
      labels: {
        cancel: 'Annulla',
        confirm: 'Elimina codice sconto',
      },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteCoupon(code),
    });
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    setLayoutProps({ title: 'Codici sconto' });
  }, [setLayoutProps]);

  return (
    <>
      <Button
        mb="lg"
        onClick={handleCreateCouponClick}
        leftSection={<IconPlus />}
      >
        Crea codice sconto
      </Button>
      <DataTable
        striped
        withTableBorder
        fetching={isLoading}
        noRecordsText="Nessun codice sconto trovato"
        minHeight={data.length === 0 ? 200 : undefined}
        columns={[
          {
            accessor: 'enabled',
            title: 'Attivo',
            render: (record) => (
              <Switch
                checked={record.enabled}
                onChange={(e) =>
                  handleToggleCouponEnabled(
                    record.code,
                    e.currentTarget.checked,
                  )
                }
                disabled={isLoadingUpdate}
              />
            ),
          },
          {
            accessor: 'code',
            title: 'Codice',
          },
          {
            accessor: 'value',
            title: 'Sconto',
            render: (record) =>
              `${record.couponValueType === 'fixed' ? `€ ` : ''}${record.value.toString()}${record.couponValueType === 'percentage' ? '%' : ''}`,
          },
          {
            accessor: 'startDate',
            title: 'Data inizio',
            render: (record) =>
              dateToDateHourString(new Date(record.startDate)),
          },
          {
            accessor: 'endDate',
            title: 'Data fine',
            render: (record) =>
              record.endDate
                ? dateToDateHourString(new Date(record.endDate))
                : '-',
          },
          {
            accessor: 'limitPerUser',
            title: 'Limite per utente',
            render: (record) => record.limitPerUser?.toString() || 'Illimitato',
          },
          {
            accessor: 'categories',
            title: 'Categorie',
            render: (record) =>
              record.categories.length > 0
                ? record.categories.map((c) => c.name).join(', ')
                : '-',
          },
          {
            accessor: 'usageCount',
            title: '# utilizzi',
            render: (record) => record.usageCount.toString(),
          },
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right',
            width: '0%',
            render: (record) => (
              <Group wrap="nowrap">
                <ActionIcon
                  onClick={() => handleUpdateCouponClick(record)}
                  loading={isLoadingUpdate}
                >
                  <IconPencil />
                </ActionIcon>
                <ActionIcon
                  color="red"
                  onClick={() => handleDeleteCouponClick(record.code)}
                  loading={isLoadingDelete}
                >
                  <IconTrash />
                </ActionIcon>
              </Group>
            ),
          },
        ]}
        records={data}
      />
    </>
  );
}
